import { Box, styled } from '@mui/material'
import { getFullImageUrl } from 'utils'
import { FC, Fragment, useState } from 'react'
import { Person, Search } from 'components'
import { Pagination } from 'packages/eid-ui'
import { useTranslation } from 'react-i18next'
import { PersonSkeleton } from 'components/Skeletons/PersonSkeleton'
import { isNilOrEmpty } from 'packages/core'

const PaginationContainer = styled(Box)({
    width: '100%',
    padding: '16px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
})

const SearchWrapper = styled(Box)({
    maxWidth: '280px',
    padding: '22px 20px 0px 6px',
})

export type ResourceOwnersListProps = {
    resourceId: string
    useDataHook: any
}

const take = 10

export const ResourceOwnersList: FC<ResourceOwnersListProps> = (props) => {
    const { t } = useTranslation()

    const [searchTerm, setSearchTerm] = useState('')
    const [page, setPage] = useState(1)
    const [perPage, setPerPage] = useState(take)

    const { data, isFetching: isLoading } = props.useDataHook(
        props.resourceId,
        (page - 1) * perPage,
        perPage,
        searchTerm,
    )

    const owners = data ? data.data : undefined

    const handlePageChange = (value: any) => {
        setPage(value)
    }

    const handlePageSelection = (value: any) => {
        setPage(value)
    }

    const handleItemsPerPageChange = (value: any) => {
        setPage(1)
        setPerPage(value)
    }

    const numberOfPages = data ? Math.ceil(data.totalCount / perPage) : 0

    if (isLoading) {
        return <OwnersLoader />
    }

    const showSearch = (owners && owners.length > 0) || searchTerm.length > 0

    return (
        <Fragment>
            {showSearch && (
                <SearchWrapper>
                    <Search
                        fullWidth
                        outlined
                        placeholder={t('Search')}
                        value={searchTerm}
                        handleClear={(value: any) => {
                            handlePageChange(1)
                            setSearchTerm(value)
                        }}
                        handleSearch={(value: any) => {
                            if (value !== searchTerm) {
                                handlePageChange(1)
                                setSearchTerm(value)
                            }
                        }}
                    />
                </SearchWrapper>
            )}

            {isLoading && <OwnersLoader />}

            {!isLoading &&
                (!owners || owners.length === 0) && (
                    <Box
                        marginY="20px"
                        paddingX="20px"
                        fontSize="13px"
                        color="#7d7c7c"
                    >
                        {t('NoDataFound')}
                    </Box>
                )}

            {owners && owners.length > 0 && (
                <Box
                    display={'flex'}
                    flexWrap={'wrap'}
                    justifyContent={'flex-start'}
                    marginTop="20px"
                >
                    {owners.map((owner: any, index: any) => (
                        <Box key={index + owner.id} maxWidth="280px" marginX="8px">
                            <Person
                                size="medium"
                                name={owner.friendlyName}
                                imgSrc={getFullImageUrl(owner.imageThumbUrl)}
                                style={{ color: '#5d6870' }}
                            />
                        </Box>
                    ))}
                </Box>
            )}

            {numberOfPages > 1 && (
                <PaginationContainer>
                    <Pagination
                        count={numberOfPages}
                        size="large"
                        page={page}
                        onChange={(_: any, value: any) => handlePageChange(value)}
                        showPageSelection={true}
                        onPageSelection={handlePageSelection}
                        showItemsPerPageSelection={true}
                        onItemsPerPageChange={handleItemsPerPageChange}
                        itemsPerPage={perPage}
                        showCountStat={true}
                        totalCount={data?.totalCount}
                    />
                </PaginationContainer>
            )}
        </Fragment>
    )
}

const OwnersLoader = () => {
    return (
        <Box
            display={'flex'}
            flexWrap={'wrap'}
            justifyContent={'flex-start'}
            marginY="20px"
        >
            <Box maxWidth="280px" marginX="8px">
                <PersonSkeleton />
            </Box>

            <Box maxWidth="280px" marginX="8px">
                <PersonSkeleton />
            </Box>

            <Box maxWidth="280px" marginX="8px">
                <PersonSkeleton />
            </Box>
        </Box>
    )
}
